import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Wallet, Copy, CheckCircle, Loader2, Clock, AlertCircle, CreditCard } from "lucide-react";

const CryptoPayment = () => {
  const { paymentId } = useParams();
  const { user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState("waiting");

  const fetchPaymentStatus = useCallback(async () => {
    try {
      const response = await api.get(`/credits/crypto-status/${paymentId}`);
      setPayment(response.data);
      setStatus(response.data.status || "waiting");
      
      if (response.data.status === "paid" || response.data.status === "finished") {
        await refreshUser();
        setTimeout(() => {
          navigate(`/payment/success?payment_id=${paymentId}&type=crypto`);
        }, 2000);
      }
    } catch (error) {
      console.error("Payment status error:", error);
      toast.error(error.response?.data?.detail || (language === "tr" ? "Ödeme durumu alınamadı" : "Failed to fetch payment status"));
    } finally {
      setLoading(false);
    }
  }, [language, navigate, paymentId, refreshUser]);

  useEffect(() => {
    if (paymentId) {
      fetchPaymentStatus();
      const interval = setInterval(fetchPaymentStatus, 5000); // Check every 5 seconds
      return () => clearInterval(interval);
    }
  }, [paymentId, fetchPaymentStatus]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(language === "tr" ? "Kopyalandı!" : "Copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusIcon = () => {
    switch (status) {
      case "paid":
      case "finished":
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case "waiting":
      case "confirming":
        return <Clock className="w-8 h-8 text-yellow-500 animate-pulse" />;
      case "failed":
      case "expired":
        return <AlertCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Loader2 className="w-8 h-8 text-primary animate-spin" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "paid":
      case "finished":
        return language === "tr" ? "Ödeme Onaylandı!" : "Payment Confirmed!";
      case "waiting":
        return language === "tr" ? "Ödeme Bekleniyor" : "Waiting for Payment";
      case "confirming":
        return language === "tr" ? "Onaylanıyor..." : "Confirming...";
      case "failed":
        return language === "tr" ? "Ödeme Başarısız" : "Payment Failed";
      case "expired":
        return language === "tr" ? "Ödeme Süresi Doldu" : "Payment Expired";
      default:
        return language === "tr" ? "Kontrol Ediliyor..." : "Checking...";
    }
  };

  if (loading && !payment) {
    return (
      <div className="min-h-screen grid-texture flex items-center justify-center p-4">
        <div className="glass p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-mono font-bold mb-2">{language === "tr" ? "Yükleniyor..." : "Loading..."}</h1>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="min-h-screen grid-texture flex items-center justify-center p-4">
        <div className="glass p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-mono font-bold mb-2 text-red-400">{language === "tr" ? "Ödeme Bulunamadı" : "Payment Not Found"}</h1>
          <Link to="/credits">
            <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none mt-4">
              {language === "tr" ? "ABONELİK PAKETLERİ" : "SUBSCRIPTION PACKAGES"}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-texture flex items-center justify-center p-4">
      <div className="glass p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          {getStatusIcon()}
          <h1 className="text-3xl font-mono font-bold mt-4 mb-2">{getStatusText()}</h1>
          {(payment.subscription_days || payment.days) && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <CreditCard className="w-6 h-6 text-primary" />
              <span className="text-xl font-mono font-bold text-primary">{payment.subscription_days || payment.days}</span>
              <span className="text-muted-foreground">{language === "tr" ? "gün abonelik" : "days subscription"}</span>
            </div>
          )}
        </div>

        {status === "waiting" || status === "confirming" ? (
          <div className="space-y-6">
            <div className="bg-white/5 p-6 rounded border border-white/10">
              <h2 className="font-mono font-bold mb-4 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" />
                {language === "tr" ? "Ödeme Bilgileri" : "Payment Information"}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground font-mono mb-2 block">
                    {language === "tr" ? "Ödeme Adresi" : "Payment Address"}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={payment.pay_address || ""}
                      readOnly
                      className="flex-1 font-mono text-sm bg-black/50 border border-white/10 px-4 py-2 rounded-none"
                    />
                    <Button
                      onClick={() => copyToClipboard(payment.pay_address)}
                      variant="outline"
                      size="sm"
                      className="font-mono rounded-none"
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground font-mono mb-2 block">
                    {language === "tr" ? "Ödeme Miktarı" : "Payment Amount"}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={`${payment.pay_amount || "0"} ${payment.pay_currency?.toUpperCase() || ""}`}
                      readOnly
                      className="flex-1 font-mono text-lg font-bold bg-black/50 border border-white/10 px-4 py-2 rounded-none text-primary"
                    />
                    <Button
                      onClick={() => copyToClipboard(`${payment.pay_amount} ${payment.pay_currency?.toUpperCase()}`)}
                      variant="outline"
                      size="sm"
                      className="font-mono rounded-none"
                    >
                      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded">
              <p className="text-sm text-yellow-400 font-mono">
                ⚠️ {language === "tr" 
                  ? "Lütfen yukarıdaki adrese tam miktarı gönderin. Ödeme onaylandıktan sonra aboneliğiniz hesabınıza tanımlanacaktır."
                  : "Please send the exact amount to the address above. Your subscription will be added after payment confirmation."}
              </p>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground font-mono">
                {language === "tr" 
                  ? "Ödeme durumu otomatik olarak kontrol ediliyor..."
                  : "Payment status is being checked automatically..."}
              </p>
            </div>
          </div>
        ) : status === "paid" || status === "finished" ? (
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">
              {language === "tr" 
                ? "Ödeme başarıyla onaylandı! Aboneliğiniz hesabınıza tanımlandı."
                : "Payment successfully confirmed! Your subscription has been added to your account."}
            </p>
            <Link to="/dashboard">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                {language === "tr" ? "DASHBOARD'A GİT" : "GO TO DASHBOARD"}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="text-center space-y-4">
            <p className="text-red-400">
              {language === "tr" 
                ? "Ödeme başarısız oldu veya süresi doldu."
                : "Payment failed or expired."}
            </p>
            <Link to="/credits">
              <Button className="font-mono uppercase bg-primary text-black hover:bg-primary/90 rounded-none">
                {language === "tr" ? "TEKRAR DENE" : "TRY AGAIN"}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoPayment;
